import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Fetches and renders active banners for a given placement.
 * Placements: "b2c_home_hero", "b2c_strip", "b2b_home_hero".
 * Renders nothing if there are no active banners — safe to drop anywhere.
 */
export const BannerStrip = ({ placement, className = "", variant = "hero" }) => {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    api.get(`/banners?placement=${placement}`)
      .then((r) => setBanners(r.data || []))
      .catch(() => {});
  }, [placement]);

  if (banners.length === 0) return null;
  const top = banners[0];

  if (variant === "strip") {
    return (
      <a
        href={top.cta_url || "#"}
        target={top.cta_url?.startsWith("http") ? "_blank" : undefined}
        rel={top.cta_url?.startsWith("http") ? "noopener noreferrer" : undefined}
        className={`block bg-gradient-to-r from-[#970C10] to-[#7A0A0D] text-white text-center text-xs sm:text-sm font-semibold py-2 px-4 hover:opacity-95 transition-opacity duration-200 ${className}`}
        data-testid={`banner-strip-${top.slug}`}
      >
        <span className="opacity-90">{top.title}</span>
        {top.subtitle && <span className="mx-2 opacity-70">·</span>}
        {top.subtitle && <span className="opacity-90">{top.subtitle}</span>}
        {top.cta_text && (
          <span className="ml-3 underline underline-offset-2 opacity-100">{top.cta_text} →</span>
        )}
      </a>
    );
  }

  return (
    <div className={`grid gap-4 ${className}`} data-testid={`banner-hero-${placement}`}>
      {banners.slice(0, 3).map((b) => (
        <a
          key={b.slug}
          href={b.cta_url || "#"}
          target={b.cta_url?.startsWith("http") ? "_blank" : undefined}
          rel={b.cta_url?.startsWith("http") ? "noopener noreferrer" : undefined}
          className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 hover:shadow-xl transition-shadow duration-200"
          data-testid={`banner-card-${b.slug}`}
        >
          {b.image_url && (
            <img
              src={b.image_url}
              alt={b.title}
              className="w-full h-40 sm:h-52 object-cover opacity-80 group-hover:scale-[1.02] transition-transform duration-500"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/25 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 text-white">
            <p className="font-heading text-lg sm:text-xl font-bold leading-tight">{b.title}</p>
            {b.subtitle && <p className="mt-1 text-sm text-white/85 line-clamp-2">{b.subtitle}</p>}
            {b.cta_text && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold bg-[#970C10] px-3 py-1.5 rounded-full">
                {b.cta_text} →
              </p>
            )}
          </div>
        </a>
      ))}
    </div>
  );
};

export default BannerStrip;
